import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_API;

let headers;
const setTokenValues = () => {
  const token = Cookies.get("token");
  headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

// GET stats: total rooms, active rooms, today's bookings, pending requests.
// Pass { isClassroom, isLab, isBmb } to scope the counts to the selected module
// (Eduspace → isClassroom, Makers Lab → isLab, Bloomberg → isBmb, Cowork → all false).
export const getDashboardStats = createAsyncThunk(
  "dashboard/getDashboardStats",
  async (obj = {}, thunkAPI) => {
    try {
      setTokenValues();
      const isClassroom = obj?.isClassroom ? true : false;
      const isLab = obj?.isLab ? true : false;
      const isBmb = obj?.isBmb ? true : false;
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/dashboard/admin/stats?isClassroom=${isClassroom}&isLab=${isLab}&isBmb=${isBmb}`,
        { method: "GET", headers }
      );
      const data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// GET activity: upcoming bookings (next 48h) + recent activities.
// If this endpoint gets slow, split into two endpoints — the slice keeps
// upcomingBookings and recentActivities in separate state keys so the page
// binding does not need to change.
export const getDashboardActivity = createAsyncThunk(
  "dashboard/getDashboardActivity",
  async (obj = {}, thunkAPI) => {
    try {
      setTokenValues();
      const isClassroom = obj?.isClassroom ? true : false;
      const isLab = obj?.isLab ? true : false;
      const isBmb = obj?.isBmb ? true : false;
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/dashboard/admin/activity?isClassroom=${isClassroom}&isLab=${isLab}&isBmb=${isBmb}`,
        { method: "GET", headers }
      );
      const data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

const initialStateValues = {
  isStatsFetching: false,
  isStatsError: false,
  statsErrorMessage: "",
  stats: {
    totalRooms: 0,
    activeRooms: 0,
    todaysBookings: 0,
    pendingRequests: 0,
  },

  isActivityFetching: false,
  isActivityError: false,
  activityErrorMessage: "",
  upcomingBookings: [],
  recentActivities: [],
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialStateValues,
  reducers: {
    clearDashboardStates: (state) => {
      state.isStatsError = false;
      state.statsErrorMessage = "";
      state.isActivityError = false;
      state.activityErrorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDashboardStats.pending, (state) => {
      state.isStatsFetching = true;
      state.isStatsError = false;
    });
    builder.addCase(getDashboardStats.fulfilled, (state, { payload }) => {
      state.isStatsFetching = false;
      state.stats = {
        totalRooms: payload?.totalRooms ?? 0,
        activeRooms: payload?.activeRooms ?? 0,
        todaysBookings: payload?.todaysBookings ?? 0,
        pendingRequests: payload?.pendingRequests ?? 0,
      };
    });
    builder.addCase(getDashboardStats.rejected, (state, { payload }) => {
      state.isStatsFetching = false;
      state.isStatsError = true;
      state.statsErrorMessage = payload?.Message || "Failed to load stats";
    });

    builder.addCase(getDashboardActivity.pending, (state) => {
      state.isActivityFetching = true;
      state.isActivityError = false;
    });
    builder.addCase(getDashboardActivity.fulfilled, (state, { payload }) => {
      state.isActivityFetching = false;
      state.upcomingBookings = payload?.upcomingBookings || [];
      state.recentActivities = payload?.recentActivities || [];
    });
    builder.addCase(getDashboardActivity.rejected, (state, { payload }) => {
      state.isActivityFetching = false;
      state.isActivityError = true;
      state.activityErrorMessage = payload?.Message || "Failed to load activity";
    });
  },
});

export const { clearDashboardStates } = dashboardSlice.actions;
export const dashboardData = (state) => state.dashboard;
export default dashboardSlice;
